import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ArrowRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COLUMNS = [{ id: "open", label: "To do" }, { id: "in_progress", label: "In progress" }, { id: "done", label: "Done" }];

export default function HousekeepingPage() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ room: "", type: "", assignee: "" });
  const load = () => api.get("/housekeeping").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const update = async (id, body) => { await api.put(`/housekeeping/${id}`, body); load(); };
  const remove = async (id) => { await api.delete(`/housekeeping/${id}`); load(); };
  const add = async () => {
    try { await api.post("/housekeeping", form); setForm({ room: "", type: "", assignee: "" }); toast.success("Task added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add task"); }
  };

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Housekeeping</h1>
        <p className="text-muted-foreground text-sm">Guest requests and staff tasks on one board.</p>
      </div>
      <div className="bg-card border border-border rounded-md p-4 flex flex-wrap gap-3 items-center" data-testid="hk-add-form">
        <Input placeholder="Room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="w-28" data-testid="hk-add-room" />
        <Input placeholder="Task, e.g. Turn down room" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="flex-1 min-w-[200px]" data-testid="hk-add-type" />
        <Input placeholder="Assign to" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="w-40" data-testid="hk-add-assignee" />
        <Button onClick={add} data-testid="hk-add-submit"><Plus className="w-4 h-4 mr-1" /> Add task</Button>
      </div>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : (
        <div className="grid md:grid-cols-3 gap-4">
          {COLUMNS.map((col, ci) => {
            const list = items.filter((i) => i.status === col.id);
            return (
              <div key={col.id} className="bg-secondary/40 rounded-md p-3 min-h-[200px]" data-testid={`hk-col-${col.id}`}>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{col.label} · {list.length}</p>
                <div className="space-y-2">
                  {list.map((t) => (
                    <div key={t.id} className="bg-card border border-border rounded-md p-3 space-y-2" data-testid={`hk-task-${t.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-foreground">{t.type}</p>
                          <p className="text-xs text-muted-foreground">Room {t.room || "—"} · {t.source === "guest" ? t.guest_name : "Staff task"}{t.preferred_time ? ` · ${t.preferred_time}` : ""}</p>
                          {t.notes && <p className="text-xs text-foreground italic mt-1">“{t.notes}”</p>}
                        </div>
                        <button onClick={() => remove(t.id)} data-testid={`hk-delete-${t.id}`} className="text-muted-foreground hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <Input defaultValue={t.assignee} placeholder="Assign to…" className="h-8 text-xs" onBlur={(e) => e.target.value !== (t.assignee || "") && update(t.id, { assignee: e.target.value })} data-testid={`hk-assignee-${t.id}`} />
                      <div className="flex justify-between">
                        {ci > 0 ? <button onClick={() => update(t.id, { status: COLUMNS[ci - 1].id })} data-testid={`hk-back-${t.id}`} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> {COLUMNS[ci - 1].label}</button> : <span />}
                        {ci < 2 && <button onClick={() => update(t.id, { status: COLUMNS[ci + 1].id })} data-testid={`hk-next-${t.id}`} className="text-xs text-foreground flex items-center gap-1 hover:opacity-70">{COLUMNS[ci + 1].label} <ArrowRight className="w-3 h-3" /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
